import { useState } from 'react'
import { Table, useApiQuery, type Column, type CreditInfo, type CreditTransaction } from 'grabbitbot-legacy-ui'
import { paymentClient } from '../../api'
import { formatNumberN, usePaymentActions, usePaymentBasics } from './paymentFlow'
import { CreditAmount, CreditBuyPanel, DateTime, PaymentPage } from './paymentShared'
import { failureText } from '../../api/errorMessages'

function amountDigits(currency: string | null | undefined): number {
  return currency === 'credit' ? 2 : 8
}

const txColumns: Column<CreditTransaction & { index: number }>[] = [
  { key: 'index', label: '#', align: 'right', numeric: true, sortValue: (r) => r.index, format: (_v, r) => `${r.index}.` },
  { key: 'createdAt', label: 'Date', align: 'left', sortValue: (r) => r.createdAt ?? '', format: (_v, r) => <DateTime value={r.createdAt} /> },
  {
    key: 'amount',
    label: 'Amount',
    align: 'right',
    numeric: true,
    sortValue: (r) => r.amount ?? 0,
    format: (_v, r) => `${formatNumberN(r.amount, amountDigits(r.currency))} ${r.currency ?? ''}`,
  },
  {
    key: 'result',
    label: 'Result',
    align: 'left',
    sortValue: (r) => r.result ?? -1,
    format: (_v, r) => (r.result === 0 ? 'SUCCESS' : r.result === null ? '—' : `Error code #${r.result}`),
  },
  {
    key: 'balanceAfter',
    label: 'Balance after transaction',
    align: 'right',
    numeric: true,
    sortValue: (r) => r.balanceAfter ?? 0,
    format: (_v, r) => formatNumberN(r.balanceAfter, amountDigits(r.currency)),
  },
  { key: 'cause', label: 'Cause', align: 'left', sortValue: (r) => r.cause ?? '' },
  { key: 'reference', label: 'Reference', align: 'left', sortValue: (r) => r.reference ?? '' },
]

/**
 * `/payment/credit` — legacy `PaymentCreditViewComponent`: the `credit`
 * balance with "buy credits" (3–10000, USD 1 each), and the credit history
 * per currency (`GET /payment/credits`, newest first as the backend sends).
 */
export function PaymentCreditPage() {
  const basics = usePaymentBasics()
  const { generation, refetch, method, billing } = basics
  const actions = usePaymentActions()
  const credits = useApiQuery(() => paymentClient.searchCreditInfos(), [generation])
  const list: CreditInfo[] = Array.isArray(credits.data) ? credits.data : []
  const [selected, setSelected] = useState('credit')
  const creditInfo = list.find((c) => c.currency === 'credit') ?? null
  const shown = list.find((c) => c.currency === selected) ?? list[0] ?? null
  const rows = (shown?.transactions ?? []).map((t, i) => ({ ...t, index: i + 1 }))

  return (
    <PaymentPage title="Credits">
      {credits.loading && <p>Loading credit balance…</p>}
      {credits.error && (
        <p className="portal-notice portal-notice--error" role="alert">
          {failureText(credits.error, 'Could not load your credit balance')}
        </p>
      )}
      <div className="portal-grid-2">
        <section className="portal-card" aria-label="Credit balance">
          <h2>Credit balance</h2>
          <p className="portal-plan-price">
            <CreditAmount balance={creditInfo?.balance ?? 0} currency="credit" />
          </p>
          <CreditBuyPanel method={method} billing={billing} actions={actions} onChanged={refetch} />
        </section>
        <section className="portal-card" aria-label="Credit history">
          <h2>Credit history</h2>
          <nav className="portal-tabs portal-tabs--sub" role="tablist" aria-label="Currency">
            {list.map((c) => (
              <button
                key={c.currency ?? ''}
                type="button"
                role="tab"
                aria-selected={shown?.currency === c.currency}
                className={shown?.currency === c.currency ? 'portal-tab portal-tab--active' : 'portal-tab'}
                onClick={() => setSelected(c.currency ?? '')}
              >
                {c.currency} <span className="portal-chip">{formatNumberN(c.balance, amountDigits(c.currency))}</span>
              </button>
            ))}
          </nav>
          {shown && (
            <>
              <h3>
                Balance: <CreditAmount balance={shown.balance} currency={shown.currency} />
              </h3>
              {rows.length === 0 ? (
                <p className="portal-text-muted">No transactions found</p>
              ) : (
                <div className="portal-table-scroll">
                  <Table columns={txColumns} rows={rows} rowKey={(r) => r.id ?? r.index} />
                </div>
              )}
            </>
          )}
        </section>
      </div>
    </PaymentPage>
  )
}
